<!-- Prosodyファースト練習カード（メインコンポーネント） -->
<template>
  <div class="prosody-practice-card">
    <!-- 状態: idle/primed/listening/processing/review/microDrill -->
    
    
    <!-- B. 差分テキストビュー -->
    <div class="text-section" v-if="referenceText">
      <DiffTextView
        :reference-text="referenceText"
        :recognized-words="evaluation?.words || []"
        :target-word="selectedTarget"
        :missing-words="evaluation?.missingWords || []"
        @word-click="onWordClick"
      />
    </div>
    
    <!-- C. タイムライン・オーバーレイ -->
    <div class="timeline-section" v-if="evaluation?.prosodyDetails?.timelineData?.length > 0">
      <TimelineOverlay
        :timeline-data="evaluation.prosodyDetails.timelineData"
        :target-word="selectedTarget"
        :total-duration="getTotalDuration()"
        @segment-click="onTimelineClick"
      />
    </div>
    
    <!-- D. コントロールバー -->
    <div class="control-section">
      <div class="control-left">
        <!-- 再生ソース切替（将来実装） -->
      </div>
      
      <div class="control-center">
        <div class="record-controls">
          <StatusLamp :can-speak="canSpeak" />
          <button
            @click="toggleRecording"
            :disabled="isBlocked || isEvaluating || isConnecting"
            class="record-button"
            :class="{
              recording: isRecording,
              evaluating: isEvaluating,
              connecting: isConnecting,
              available: !isBlocked && !isRecording && !isEvaluating && !isConnecting
            }"
          >
            <span v-if="isRecording">⏹️ 停止</span>
            <span v-else-if="isEvaluating">⏳ 評価中...</span>
            <span v-else-if="isConnecting">🔌 接続中...</span>
            <span v-else>🎤 録音開始</span>
          </button>
        </div>
        
        <div class="recording-timer" v-if="isRecording">
          {{ recordingTime }}s
        </div>
        <div class="wait-hint" v-else-if="isBlocked && !isConnecting">
          次の評価開始までお待ちください...
        </div>
      </div>
      
      <div class="control-right">
        <!-- 共有ボタン（90点以上で有効） -->
        <button
          v-if="evaluation && canShare"
          @click="shareRecording"
          class="share-button"
          :disabled="isSaving || isShared"
        >
          <span v-if="isSaving">💾 保存中...</span>
          <span v-else-if="isShared">✅ 保存済み</span>
          <span v-else>📤 共有する</span>
        </button>
        
        <!-- 7秒で直すボタン（非表示） -->
        <!--
        <button
          v-if="selectedTarget && evaluation"
          @click="openQuickDrill"
          class="quick-drill-button"
          :disabled="isDrillActive"
        >
          ⚡ 7秒で直す
        </button>
        -->
      </div>
    </div>
    
    <!-- E. 7秒ミニドリル（モーダル） -->
    <QuickDrill
      v-if="isDrillActive && selectedTarget"
      :target-word="selectedTarget"
      :gate-type="gateType"
      :reference-text="referenceText"
      @close="closeQuickDrill"
      @success="onDrillSuccess"
    />
    
    <!-- F. 単語練習モーダル -->
    <WordPracticeModal
      :word="selectedWordForPractice"
      :is-open="isWordPracticeModalOpen"
      @close="closeWordPracticeModal"
    />
    
    <!-- F. 詳細パネル（折りたたみ・非表示） -->
    <!--
    <div class="details-panel" v-if="showDetails && evaluation">
      <button @click="showDetails = false" class="collapse-button">
        ▲ 詳細を隠す
      </button>
      <!- 既存の音素分析UIをここに配置（TrainingCard.vueから移植可能） ->
    </div>
    
    <div class="details-toggle" v-else-if="evaluation">
      <button @click="showDetails = true" class="expand-button">
        ▼ 詳細を見る
      </button>
    </div>
    -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import DiffTextView from './DiffTextView.vue'
import TimelineOverlay from './TimelineOverlay.vue'
import QuickDrill from './QuickDrill.vue'
import WordPracticeModal from './WordPracticeModal.vue'
import { AzureSpeechEvaluator } from '../utils/azureSpeechEvaluator'
import { selectTargetWord } from '../utils/targetWordSelector'
import { supabase } from '../supabaseClient.js'
import { getApiBase } from '../utils/apiBase'
import StatusLamp from './StatusLamp.vue'
import { useNextUtteranceCue } from '../composables/useNextUtteranceCue'

const props = defineProps({
  task: {
    type: Object,
    required: true
  },
  sessionStats: {
    type: Object,
    default: () => ({})
  },
  totalStats: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['next', 'previous', 'complete'])

// 状態
const state = ref('idle') // idle/primed/listening/processing/review/microDrill
const isRecording = ref(false)
const isEvaluating = ref(false)
const isConnecting = ref(false)
const {
  canSpeak,
  isBlocked,
  blockNow,
  readyNow,
  blockThenReady,
  reset: resetCue
} = useNextUtteranceCue()
const isDrillActive = ref(false)
const showDetails = ref(false)
const recordingTime = ref(0)
const isSaving = ref(false)
const isShared = ref(false)

// 評価結果
const evaluation = ref(null)
const selectedTarget = ref(null)
const gateType = ref(null)
const referenceText = ref('')

// 単語練習モーダル
const isWordPracticeModalOpen = ref(false)
const selectedWordForPractice = ref(null)

// インスタンス
let speechEvaluator = null
let recordingTimer = null
let recordingTimeInterval = null

// 初期化
onMounted(async () => {
  referenceText.value = props.task.sentence || ''
  await initializeEvaluator()
  canSpeak.value = true
})

onUnmounted(() => {
  cleanup()
  resetCue()
})

// 評価器初期化
const initializeEvaluator = async () => {
  if (!speechEvaluator) {
    speechEvaluator = new AzureSpeechEvaluator()
  }
}

// クリーンアップ
const cleanup = () => {
  if (recordingTimer) {
    clearInterval(recordingTimer)
    recordingTimer = null
  }
  if (recordingTimeInterval) {
    clearInterval(recordingTimeInterval)
    recordingTimeInterval = null
  }
  if (speechEvaluator) {
    speechEvaluator.cleanup()
  }
  resetCue()
}

// 録音開始/停止
const toggleRecording = async () => {
  if (isConnecting.value || isEvaluating.value) return
  if (isRecording.value) {
    await stopRecording()
  } else {
    blockNow({ playBeep: false })
    await startRecording()
  }
}

// 録音停止条件の管理
let silenceTimer = null // 無音検知タイマー（5秒）
const MAX_RECORDING_TIME = 30 // 最大録音時間（秒）
let lastSoundTime = Date.now()

const startRecordingTimer = () => {
  if (recordingTimeInterval) {
    clearInterval(recordingTimeInterval)
    recordingTimeInterval = null
  }
  recordingTime.value = 0
  lastSoundTime = Date.now()

  recordingTimeInterval = setInterval(() => {
    recordingTime.value++

    if (recordingTime.value >= MAX_RECORDING_TIME) {
      console.log('⏰ 最大録音時間（30秒）に達しました')
      stopRecording()
      return
    }

    const silenceDuration = Date.now() - lastSoundTime
    if (silenceDuration >= 5000 && !silenceTimer) {
      console.log('🔇 無音が5秒続きました')
      stopRecording()
    }
  }, 1000)
}

// スコア更新処理（recognizedイベントごとに呼ばれる）
const updateScore = (result) => {
  if (!result || result.overall === undefined) return
  
  // 評価結果を標準化形式で保存
  evaluation.value = result

  // 語スコアと音素詳細をログ
  try {
    const wordScores = (result.words || []).map(w => ({ word: w.word || w.Word, score: w.score ?? Math.round(w.accuracy || w.AccuracyScore || 0) }))
    console.log('🧩 語スコア一覧:', wordScores)
    const phonemePreview = (result.phonemes || []).map(p => ({ phoneme: p.phoneme, word: p.word, accuracy: p.accuracy, position: p.position, duration: p.duration }))
    console.log('🔠 音素詳細（抜粋）:', phonemePreview)
  } catch (e) {
    console.warn('ログ生成エラー（語/音素）:', e)
  }
  
  // ターゲット語選定（最新の結果で更新）
  const targetResult = selectTargetWord(result, {
    weakPhonemes: [],
    frequentWords: []
  })
  
  selectedTarget.value = targetResult.targetWord
  gateType.value = targetResult.gateType
  console.log('🎯 ターゲット語選定:', { target: selectedTarget.value, gateType: gateType.value })
  
  // 無音タイマーをリセット（音声が検出された）
  lastSoundTime = Date.now()
  if (silenceTimer) {
    clearTimeout(silenceTimer)
    silenceTimer = null
  }
  
  // 親にスコア更新を通知
  let prosodyScore = null
  if (result.prosody !== null && result.prosody !== undefined) {
    if (typeof result.prosody === 'number') {
      prosodyScore = result.prosody
    } else if (typeof result.prosody === 'object' && result.prosody.overall !== undefined) {
      prosodyScore = result.prosody.overall
    }
  }
  
  emit('complete', {
    overall: result.overall ?? 0,
    accuracy: result.accuracy ?? 0,
    fluency: result.fluency ?? 0,
    completeness: result.completeness ?? 0,
    prosody: prosodyScore
  })

  // 評価表示と同時に次発話をブロック→1秒後に再開
  blockThenReady(1000)
}

// 録音開始
const startRecording = async () => {
  try {
    state.value = 'primed'
    isConnecting.value = true
    isRecording.value = false
    isEvaluating.value = false
    recordingTime.value = 0
    lastSoundTime = Date.now()

    const realtimeText = ref('')
    
    const evaluationPromise = speechEvaluator.evaluateWithSDK(
      referenceText.value,
      (text) => {
        realtimeText.value = text
        lastSoundTime = Date.now()
      },
      (result) => {
        updateScore(result)
      },
      {
        onReady: () => {
          if (isRecording.value) return
          isConnecting.value = false
          isRecording.value = true
          state.value = 'listening'
          readyNow({ playBeep: true })
          startRecordingTimer()
        }
      }
    )
    
    await evaluationPromise
    
  } catch (error) {
    console.error('録音・評価エラー:', error)
    
    // エラーの種類に応じて処理を分岐
    const errorCode = error.code || ''
    const errorType = error.type || ''
    
    // 無音タイムアウトの場合はメッセージを表示しない（5秒ルールで自動停止するため）
    if (errorCode === 'SILENCE_TIMEOUT' || errorType === 'silence') {
      console.log('🔇 無音のため評価を終了（メッセージ表示なし）')
      // メッセージは表示せず、録音を停止するだけ
      stopRecording()
      return
    }
    
    // マイク権限エラーの場合はメッセージを表示
    if (errorCode === 'MICROPHONE_PERMISSION_DENIED' || errorType === 'permission') {
      alert(`エラー: ${error.message}\n\nブラウザの設定からマイクの許可をお願いします。`)
    } else {
      // その他のエラー（ネットワークエラーなど）はメッセージを表示
    alert(`エラー: ${error.message}`)
    }
    
    state.value = 'idle'
    isRecording.value = false
    isEvaluating.value = false
    isConnecting.value = false
    resetCue()
    readyNow({ playBeep: false })
  } finally {
    isConnecting.value = false
    if (recordingTimeInterval) {
      clearInterval(recordingTimeInterval)
      recordingTimeInterval = null
    }
    if (silenceTimer) {
      clearTimeout(silenceTimer)
      silenceTimer = null
    }
  }
}

// 録音停止
const stopRecording = async () => {
  if (!isRecording.value && !isConnecting.value) return
  
  isConnecting.value = false
  const wasRecording = isRecording.value
  isRecording.value = false
  if (wasRecording) {
    isEvaluating.value = true
  }
  
  if (recordingTimeInterval) {
    clearInterval(recordingTimeInterval)
    recordingTimeInterval = null
  }
  
  if (silenceTimer) {
    clearTimeout(silenceTimer)
    silenceTimer = null
  }
  
  // API切断と録音データ取得
  if (speechEvaluator) {
    const finalResult = await speechEvaluator.stopRealtimeEvaluation()

    // 最終結果ログ（語スコア/音素）
    if (finalResult) {
      try {
        const wordScores = (finalResult.words || []).map(w => ({ word: w.word || w.Word, score: w.score ?? Math.round(w.accuracy || w.AccuracyScore || 0) }))
        console.log('🧩 最終 語スコア一覧:', wordScores)
        const phonemePreview = (finalResult.phonemes || []).map(p => ({ phoneme: p.phoneme, word: p.word, accuracy: p.accuracy, position: p.position, duration: p.duration }))
        console.log('🔠 最終 音素詳細（抜粋）:', phonemePreview)
      } catch (e) {
        console.warn('ログ生成エラー（最終 語/音素）:', e)
      }
    }
    
    // 最終結果を保存（録音データを含む）
    if (finalResult && finalResult.overall !== undefined) {
      evaluation.value = finalResult
      evaluation.value.audioBlob = finalResult.audioBlob
      
      // ターゲット語選定
      const targetResult = selectTargetWord(finalResult, {
        weakPhonemes: [],
        frequentWords: []
      })
      
      selectedTarget.value = targetResult.targetWord
      gateType.value = targetResult.gateType
      
      // 最終スコアを通知
      let prosodyScore = null
      if (finalResult.prosody !== null && finalResult.prosody !== undefined) {
        if (typeof finalResult.prosody === 'number') {
          prosodyScore = finalResult.prosody
        } else if (typeof finalResult.prosody === 'object' && finalResult.prosody.overall !== undefined) {
          prosodyScore = finalResult.prosody.overall
        }
      }
      
      emit('complete', {
        overall: finalResult.overall ?? 0,
        accuracy: finalResult.accuracy ?? 0,
        fluency: finalResult.fluency ?? 0,
        completeness: finalResult.completeness ?? 0,
        prosody: prosodyScore
      })
    }
    
    state.value = 'review'
    isEvaluating.value = false
  }

  if (!isBlocked.value) {
    readyNow({ playBeep: false })
  }
}

// 単語クリック
const onWordClick = (segment) => {
  console.log('単語クリック:', segment)
  // 単語練習モーダルを開く
  selectedWordForPractice.value = segment
  isWordPracticeModalOpen.value = true
}

// 単語練習モーダルを閉じる
const closeWordPracticeModal = () => {
  isWordPracticeModalOpen.value = false
  selectedWordForPractice.value = null
}

// タイムラインクリック
const onTimelineClick = (segment) => {
  console.log('タイムラインクリック:', segment)
}

// クイックドリル開始
const openQuickDrill = () => {
  if (!selectedTarget.value) return
  isDrillActive.value = true
  state.value = 'microDrill'
}

// クイックドリル終了
const closeQuickDrill = () => {
  isDrillActive.value = false
  state.value = 'review'
}

// ドリル成功
const onDrillSuccess = () => {
  closeQuickDrill()
  // 成功処理（次へ進むなど）
  // 評価結果があればそのスコアを渡す、なければ全体の評価結果を渡す
  if (evaluation.value && evaluation.value.overall !== undefined) {
    // Prosodyスコアの取得（数値またはオブジェクトの両方に対応）
    let prosodyScore = null
    if (evaluation.value.prosody !== null && evaluation.value.prosody !== undefined) {
      if (typeof evaluation.value.prosody === 'number') {
        prosodyScore = evaluation.value.prosody
      } else if (typeof evaluation.value.prosody === 'object' && evaluation.value.prosody.overall !== undefined) {
        prosodyScore = evaluation.value.prosody.overall
      }
    }
    
    emit('complete', {
      overall: evaluation.value.overall ?? 0,
      accuracy: evaluation.value.accuracy ?? 0,
      fluency: evaluation.value.fluency ?? 0,
      completeness: evaluation.value.completeness ?? 0,
      prosody: prosodyScore
    })
  } else {
    emit('complete')
  }
}

// 総時間取得
const getTotalDuration = () => {
  if (!evaluation.value?.prosodyDetails?.timelineData?.length) return 1
  
  const lastSegment = evaluation.value.prosodyDetails.timelineData[
    evaluation.value.prosodyDetails.timelineData.length - 1
  ]
  return lastSegment ? lastSegment.end : 1
}

// 共有可能かどうか（90点以上かつ録音データあり）
const canShare = computed(() => {
  return evaluation.value && 
         evaluation.value.overall >= 90 && 
         evaluation.value.audioBlob
})

// 共有ボタンクリック時の処理
const shareRecording = async () => {
  if (!canShare.value || !evaluation.value?.audioBlob) {
    return
  }
  
  try {
    isSaving.value = true
    console.log('📤 共有ボタンクリック: 録音データを保存します')
    await savePerfectRecording(evaluation.value.audioBlob, props.task.id)
    isShared.value = true
    console.log('✅ 保存完了')
  } catch (error) {
    console.error('❌ 保存エラー:', error)
    alert(`保存に失敗しました: ${error.message}`)
  } finally {
    isSaving.value = false
  }
}

// 90点以上の録音データ保存（ユーザー操作時）
const savePerfectRecording = async (audioBlob, taskId) => {
  try {
    console.log('💾 録音データ保存開始:', { 
      size: audioBlob.size, 
      type: audioBlob.type, 
      taskId,
      score: evaluation.value?.overall 
    })
    
    // 1. R2署名URL取得（PUT用）
    const session = await supabase.auth.getSession()
    if (!session.data.session) {
      throw new Error('認証が必要です')
    }
    
    const fileName = `training_perfect_${taskId}_${Date.now()}.${audioBlob.type.split('/')[1]?.split(';')[0] || 'webm'}`
    
    const signResponse = await fetch(`${getApiBase()}/api/storage/sign`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.data.session.access_token}`
      },
      body: JSON.stringify({
        fileName,
        contentType: audioBlob.type
      })
    })
    
    if (!signResponse.ok) {
      throw new Error('署名URL取得に失敗しました')
    }
    
    const { signedUrl, publicUrl } = await signResponse.json()
    
    // 2. R2にアップロード
    const uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': audioBlob.type
      },
      body: audioBlob
    })
    
    if (!uploadResponse.ok) {
      throw new Error('アップロードに失敗しました')
    }
    
    console.log('✅ R2アップロード完了:', publicUrl)
    
    // 3. training_resultsテーブルに保存
    const { data: user } = await supabase.auth.getUser()
    if (!user.user) {
      throw new Error('ユーザー情報が取得できません')
    }
    
    const { error: insertError } = await supabase
      .from('training_results')
      .insert({
        user_id: user.user.id,
        task_id: taskId,
        score: evaluation.value.overall, // 実際のスコアを保存
        accuracy: evaluation.value.accuracy,
        fluency: evaluation.value.fluency,
        completeness: evaluation.value.completeness,
        prosody: evaluation.value.prosody,
        audio_url: publicUrl,
        is_shared: true, // ユーザーが明示的に共有したのでtrue
        metadata: {
          overall: evaluation.value.overall,
          words: evaluation.value.words,
          phonemes: evaluation.value.phonemes
        }
      })
    
    if (insertError) {
      console.error('❌ training_results保存エラー:', insertError)
      // アップロードは成功しているがDB保存に失敗した場合も警告のみ
      console.warn('⚠️ 録音は保存されましたが、データベースへの記録に失敗しました')
    } else {
      console.log('✅ training_results保存完了')
    }
    
  } catch (error) {
    console.error('❌ 録音データ保存エラー:', error)
    // 保存失敗は警告のみ（評価結果は既に表示済み）
    alert(`⚠️ 録音データの保存に失敗しました: ${error.message}`)
  }
}
</script>

<style scoped>
.prosody-practice-card {
  max-width: 800px;
  margin: 0 auto;
  padding: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.score-section {
  margin-bottom: 1.5rem;
}

.text-section {
  margin-bottom: 1.5rem;
}

.timeline-section {
  margin-bottom: 1.5rem;
}

.control-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.control-left,
.control-right {
  flex: 0 0 auto;
}

.control-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.record-controls {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.record-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 140px;
}

.record-button.connecting {
  background: #6b7280;
  color: #f9fafb;
  transform: translateY(1px);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.18);
  cursor: wait;
}

.record-button.connecting:disabled {
  background: #6b7280;
  color: #f9fafb;
  opacity: 1;
}

.record-button:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.record-button.recording {
  background: #ef4444;
}

.record-button.recording:hover {
  background: #dc2626;
}

.record-button.available {
  background: #3b82f6;
}

.record-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.recording-timer {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: bold;
}

.wait-hint {
  font-size: 0.85rem;
  color: #6b7280;
}

.share-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 0.5rem;
}

.share-button:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.share-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.quick-drill-button {
  background: #10b981;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.quick-drill-button:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.quick-drill-button:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.details-panel {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.collapse-button,
.expand-button {
  background: transparent;
  border: 1px solid #d1d5db;
  color: #6b7280;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
}

.collapse-button:hover,
.expand-button:hover {
  background: #e5e7eb;
}

.details-toggle {
  margin-top: 1rem;
  text-align: center;
}

@media (max-width: 768px) {
  .prosody-practice-card {
    padding: 1rem;
  }
  
  .control-section {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .control-left,
  .control-right {
    width: 100%;
  }
  
  .quick-drill-button {
    width: 100%;
  }
}
</style>

